'use client';

import React, { useMemo } from 'react';
// eslint-disable-next-line import/no-unresolved
import { useHits } from 'react-instantsearch-next';
import ProductCardGridUI from 'components/consumer/ProductCardGridUI';
import ProductCardStatic from 'components/consumer/ProductCardStatic';
import ProductCardStaticPlaceholder from 'components/consumer/ProductCardStaticPlaceholder';
import useProductCardDataNext from 'hooks/useProductCardDataNext';

/**
 * Search Modal Product Grid component
 * Uses GraphQL data for full product details with images and prices
 * Makes one batch GraphQL call for all products
 */
const SearchModalProductGridNext = () => {
  const { hits } = useHits();

  const limitedHits = useMemo(() => hits.slice(0, 3), [hits]);

  const productIds = useMemo(
    () => limitedHits.map(hit => parseInt(hit.product_id || hit.objectID, 10)),
    [limitedHits]
  );

  // Fetch and process product data
  const { products, loading } = useProductCardDataNext({ productIds });

  const productMap = useMemo(
    () =>
      products?.reduce((acc, product) => {
        acc[product.id] = product;
        return acc;
      }, {}) || {},
    [products]
  );

  if (!limitedHits || limitedHits.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">No products found</div>
    );
  }

  if (loading) {
    return (
      <ProductCardGridUI
        columnsOnDesktop="3"
        columnsOnMobile="2"
        limitWidth={false}
      >
        {limitedHits.map(hit => (
          <ProductCardStaticPlaceholder key={hit.objectID} />
        ))}
      </ProductCardGridUI>
    );
  }

  return (
    <ProductCardGridUI
      columnsOnDesktop="3"
      columnsOnMobile="2"
      limitWidth={false}
    >
      {limitedHits.map((hit, index) => {
        const productId = parseInt(hit.product_id || hit.objectID, 10);
        const product = {
          ...productMap[productId],
          name: hit?.name ?? productMap[productId]?.name ?? '',
        };

        if (!product || !product.id) {
          return <ProductCardStaticPlaceholder key={hit.objectID} />;
        }

        return (
          <ProductCardStatic
            key={hit.objectID}
            {...product}
            position={index + 1}
            layout="minimal"
            analyticsComponentName="search-product-card"
          />
        );
      })}
    </ProductCardGridUI>
  );
};

export default SearchModalProductGridNext;
